import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { createGuestPaymentIntent } from "../api/orders";
import { getGuestPickupLocations } from "../api/products";
import { DeliveryDateCalendar } from "../components/DeliveryDateCalendar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useCart } from "../context/CartContext";
import { getGuestId, resetGuestId } from "../utils/guest";
import {
  getNextAllowedDate,
  getTomorrowDate,
  isBeforeDay,
  isDateAllowed,
  parseDateInputValue,
  toDateInputValue,
} from "../utils/deliveryDates";
import { formatCurrency } from "../utils/format";
import { safeGetItem, safeRemoveItem } from "../utils/safeStorage";

function isValidEmail(value) {
  return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function getAustralianPostcode(value) {
  return String(value || "").match(/\b\d{4}\b/)?.[0] || "3000";
}

function readStoredCheckoutSelection() {
  try {
    return JSON.parse(safeGetItem("guestCheckoutSelection", "null"));
  } catch (storageError) {
    console.warn("Checkout selection could not be read from browser storage.", storageError);
    return null;
  }
}

function clearStoredCheckoutSelection() {
  safeRemoveItem("guestCheckoutSelection");
}

function StripePaymentForm({
  amount,
  checkoutData,
  clientSecret,
  onClose,
  onError,
  onSuccess,
  placingOrder,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    console.log("CardElement modal form mounted", {
      hasStripe: Boolean(stripe),
      hasElements: Boolean(elements),
    });
  }, [elements, stripe]);

  async function handlePayAndOrder() {
    if (!stripe || !elements) {
      onError("Payment form is still loading.");
      return;
    }

    onError("");
    setPaymentError("");
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onError("Card form is still loading.");
      setPaymentError("Card form is still loading.");
      return;
    }

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: checkoutData.name,
          phone: checkoutData.phone,
          email: checkoutData.email || undefined,
          address: {
            country: "AU",
            postal_code: checkoutData.postalCode || "3000",
          },
        },
      },
    });
    console.log("confirmCardPayment result", result);
    console.log("paymentIntent.id", result.paymentIntent?.id || "");
    console.log("paymentIntent.status", result.paymentIntent?.status || "");

    if (result.error) {
      setPaymentError(result.error.message || "Payment failed.");
      onError(result.error.message || "Payment failed.");
      return;
    }

    if (result.paymentIntent?.status !== "succeeded") {
      setPaymentError("Payment did not complete.");
      onError("Payment did not complete.");
      return;
    }

    await onSuccess(result.paymentIntent.id);
  }

  return (
    <Card className="glass-panel flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between border-b border-border/60">
        <CardTitle className="text-xl">Payment</CardTitle>
        <button
          className="rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-secondary"
          onClick={onClose}
          type="button"
        >
          Close
        </button>
      </CardHeader>
      <CardContent className="space-y-5 overflow-y-auto pt-6">
        <div className="rounded-2xl border border-border bg-background px-4 py-4">
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  color: "#1f2933",
                  fontFamily: "Arial, Helvetica, sans-serif",
                  fontSize: "16px",
                  "::placeholder": {
                    color: "#607080",
                  },
                },
                invalid: {
                  color: "#dc2626",
                },
              },
            }}
            onChange={(event) => {
              setPaymentError(event.error?.message || "");
            }}
            onReady={() => {
              console.log("CardElement ready");
              setPaymentError("");
            }}
          />
        </div>
        {paymentError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {paymentError}
          </div>
        ) : null}
        <div className="flex items-center justify-between rounded-3xl bg-secondary/70 p-4 text-base font-semibold">
          <span>Payment amount</span>
          <span>{formatCurrency(amount)}</span>
        </div>
        <Button
          className="w-full sm:w-auto"
          disabled={!stripe || placingOrder}
          onClick={handlePayAndOrder}
          size="lg"
          type="button"
        >
          {placingOrder ? "Placing order..." : "Pay and place order"}
        </Button>
      </CardContent>
    </Card>
  );
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { cartGSTTotal, cartGrandTotal, cartItems, cartSubtotal, checkout, error: cartError, loading, refreshGuestId, resetCart } = useCart();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [pickupId, setPickupId] = useState("");
  const [pickupDate, setPickupDate] = useState(() => toDateInputValue(getNextAllowedDate()));
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [preparingPayment, setPreparingPayment] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [isCheckoutComplete, setIsCheckoutComplete] = useState(false);
  const [error, setError] = useState("");
  const [checkoutSelection, setCheckoutSelection] = useState(state || null);
  const [pickupLocations, setPickupLocations] = useState([]);
  const minimumPickupDate = useMemo(() => getTomorrowDate(), []);

  const selectedPickup = useMemo(
    () =>
      pickupLocations.find((item) => String(item.pickupId) === String(pickupId)) ||
      (String(checkoutSelection?.pickup?.pickupId || "") === String(pickupId)
        ? checkoutSelection.pickup
        : null),
    [checkoutSelection, pickupId, pickupLocations]
  );

  const stripeLoadOptions = useMemo(
    () => (paymentConfig?.StripeAccountId ? { stripeAccount: paymentConfig.StripeAccountId } : undefined),
    [paymentConfig?.StripeAccountId]
  );
  const stripePromise = useMemo(() => {
    if (!paymentConfig?.PublishableKey) {
      return null;
    }

    console.log("loadStripe account option", stripeLoadOptions || {});
    return loadStripe(paymentConfig.PublishableKey, stripeLoadOptions);
  }, [paymentConfig?.PublishableKey, stripeLoadOptions]);
  const stripeElementsOptions = useMemo(
    () => (paymentConfig?.ClientSecret ? { clientSecret: paymentConfig.ClientSecret } : null),
    [paymentConfig?.ClientSecret]
  );

  useEffect(() => {
    console.log("paymentConfig state", paymentConfig);
    console.log("modal open state", Boolean(paymentConfig && stripePromise));
    if (paymentConfig) {
      console.log("ClientSecret", paymentConfig.ClientSecret);
      console.log("PublishableKey", paymentConfig.PublishableKey);
      console.log("StripeAccountId", paymentConfig.StripeAccountId || "");
    }
  }, [paymentConfig, stripePromise]);

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    async function loadPickupLocations() {
      try {
        const response = await getGuestPickupLocations(controller.signal);
        if (!ignore) {
          setPickupLocations(response);
        }
      } catch (requestError) {
        if (!ignore) {
          setPickupLocations([]);
          setError("Pickup locations could not be loaded.");
        }
      }
    }

    loadPickupLocations();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const storedSelection = checkoutSelection || readStoredCheckoutSelection();
    if (storedSelection?.pickup?.pickupId) {
      setCheckoutSelection(storedSelection);
      setPickupId(storedSelection.pickup.pickupId);
      console.log("checkout restored pickup location", storedSelection.pickup);
    }
  }, [checkoutSelection]);

  useEffect(() => {
    if (!loading && !cartItems.length && !preparingPayment && !placingOrder && !isCheckoutComplete) {
      navigate("/cart", { replace: true });
    }
  }, [cartItems.length, isCheckoutComplete, loading, navigate, placingOrder, preparingPayment]);

  function validateForm() {
    if (!cartItems.length) {
      return "Cart cannot be empty.";
    }
    if (!name.trim()) {
      return "Name is required.";
    }
    if (!phone.trim()) {
      return "Phone is required.";
    }
    if (!isValidEmail(email.trim())) {
      return "Enter a valid email address.";
    }
    if (!selectedPickup?.pickupId) {
      return "Pickup location is required.";
    }
    if (!selectedPickup.warehouse) {
      return "Warehouse is required.";
    }
    if (!pickupDate) {
      return "Pickup date is required.";
    }
    const selectedDate = parseDateInputValue(pickupDate);
    if (!selectedDate || isBeforeDay(selectedDate, minimumPickupDate) || !isDateAllowed(selectedDate)) {
      return "Selected pickup date is not available for delivery.";
    }
    return "";
  }

  async function handlePreparePayment(event) {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setPreparingPayment(true);
      setError("");
      console.log("payload sent to payment intent", {
        pickupId: selectedPickup.pickupId,
        warehouse: selectedPickup.warehouseCode || selectedPickup.warehouse,
      });
      const response = await createGuestPaymentIntent(
        selectedPickup.pickupId,
        ""
      );
      console.log("payment API response", response);
      console.log("ClientSecret", response.ClientSecret);
      console.log("PublishableKey", response.PublishableKey);
      console.log("StripeAccountId", response.StripeAccountId || "");
      if (!response.ClientSecret || !response.PublishableKey) {
        throw new Error("Payment setup is missing Stripe configuration.");
      }
      setPaymentConfig(response);
    } catch (paymentError) {
      setError(paymentError.message || "Payment could not be started.");
    } finally {
      setPreparingPayment(false);
    }
  }

  async function placePaidOrder(paymentIntentId) {
    try {
      setPlacingOrder(true);
      setError("");
      const response = await checkout({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        pickup: selectedPickup,
        pickupDate,
        paymentIntentId,
        paymentStatus: "succeeded",
        cartItems,
        subtotal: cartSubtotal,
        gstTotal: cartGSTTotal,
        grandTotal: cartGrandTotal,
      });

      setIsCheckoutComplete(true);
      setPaymentConfig(null);
      resetCart();
      resetGuestId();
      const newGuestId = getGuestId();
      refreshGuestId();
      console.log("GuestId reset after successful order. New GuestId:", newGuestId);
      clearStoredCheckoutSelection();
      navigate("/success", {
        replace: true,
        state: {
          orderId:
            response.Result?.OrderNumber ||
            response.Result?.orderNumber ||
            response.Result?.OrderId ||
            response.Result?.orderId ||
            "Checkout complete",
          email: email.trim(),
          pickupLocation: selectedPickup?.pickupName,
          pickupDate,
          amount: cartGrandTotal,
        },
      });
    } catch (checkoutError) {
      setError(checkoutError.message || "Order could not be placed.");
    } finally {
      setPlacingOrder(false);
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
      <div className="space-y-6">
        <form className="space-y-6" onSubmit={handlePreparePayment}>
          <Card className="glass-panel">
            <CardHeader className="flex flex-col gap-3 border-b border-border/60 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <button
                  className="mb-2 inline-flex items-center text-sm font-semibold text-primary transition hover:underline"
                  onClick={() => navigate("/cart")}
                  type="button"
                >
                  &larr; Back to cart
                </button>
                <CardTitle className="text-2xl">Checkout</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="field-label" htmlFor="name">
                  Name
                </label>
                <input
                  className="field-input"
                  id="name"
                  onChange={(event) => {
                    setName(event.target.value);
                    setPaymentConfig(null);
                  }}
                  value={name}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="phone">
                  Phone
                </label>
                <input
                  className="field-input"
                  id="phone"
                  onChange={(event) => {
                    setPhone(event.target.value);
                    setPaymentConfig(null);
                  }}
                  value={phone}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="email">
                  Email
                </label>
                <input
                  className="field-input"
                  id="email"
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setPaymentConfig(null);
                  }}
                  type="email"
                  value={email}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="location">
                  Pickup Location
                </label>
                <select
                  className="field-input"
                  id="location"
                  onChange={(event) => {
                    setPickupId(event.target.value);
                    setPaymentConfig(null);
                  }}
                  value={pickupId}
                >
                  {pickupLocations
                    .map((item) => (
                    <option key={item.pickupId} value={item.pickupId}>
                      {item.warehouse ? `${item.warehouse}` : ""} ({item.pickupAddress}) 
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="pickupDate">
                  Pickup Date :- (Pick up between 9am and 3pm)
                </label>
                <DeliveryDateCalendar
                  minDate={minimumPickupDate}
                  onChange={(dateValue) => {
                    setPickupDate(dateValue);
                    setPaymentConfig(null);
                  }}
                  value={pickupDate}
                />
              </div>
              {selectedPickup ? (
                <div className="sm:col-span-2 rounded-2xl border border-border bg-background/70 px-4 py-3 text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground">{selectedPickup.pickupAddress}</p>
                  {/* <p className="mt-1">Warehouse: {selectedPickup.warehouse || "-"}</p> */}
                </div>
              ) : null}
              {error || cartError ? (
                <div className="sm:col-span-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error || cartError}
                </div>
              ) : null}
              <div className="sm:col-span-2">
                <Button
                  className="w-full sm:w-auto"
                  disabled={preparingPayment || loading || !cartItems.length}
                  size="lg"
                  type="submit"
                >
                  {preparingPayment ? "Preparing payment..." : "Continue to payment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {paymentConfig && stripePromise && stripeElementsOptions ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-foreground/45 px-4 py-6 backdrop-blur-sm">
            <Elements
              key={paymentConfig.ClientSecret}
              options={stripeElementsOptions}
              stripe={stripePromise}
            >
              <StripePaymentForm
                amount={paymentConfig.Amount || cartGrandTotal}
                checkoutData={{
                  name: name.trim(),
                  phone: phone.trim(),
                  email: email.trim(),
                  postalCode: getAustralianPostcode(selectedPickup?.pickupAddress),
                }}
                clientSecret={paymentConfig.ClientSecret}
                onClose={() => setPaymentConfig(null)}
                onError={setError}
                onSuccess={placePaidOrder}
                placingOrder={placingOrder}
              />
            </Elements>
          </div>
        ) : null}
      </div>

      <aside>
        <Card className="glass-panel sticky top-24">
          <CardHeader className="border-b border-border/60">
            <CardTitle className="text-xl">Order summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div className="flex items-center justify-between gap-3 text-sm" key={item.cartKey || `${item.productId}-${item.uomId || 0}`}>
                  <span className="text-muted-foreground">
                    {item.name}
                    {item.uomDesc ? ` (${item.uomDesc})` : ""} x {item.qty}
                  </span>
                  <span className="font-semibold">{formatCurrency(item.total)}</span>
                </div>
              ))}
            </div>
            <div className="rounded-3xl bg-secondary/70 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Subtotal</span>
                <span>{formatCurrency(cartSubtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>GST</span>
                <span>{formatCurrency(cartGSTTotal)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border/70 pt-2 text-base font-semibold">
                <span>Grand Total</span>
                <span>{formatCurrency(cartGrandTotal)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
